/**
 * Series edit bounding boxes.
 *
 * A base slide series may carry a list of axis-aligned rectangles in its own
 * level-0 pixel coordinates. While a module that opts into edit regions is
 * active (every `label_raster` module, and `vector` modules with an
 * `edit_region_domain`), edits are confined to these boxes and everything
 * outside is dimmed. A series with no matching boxes stays fully editable.
 *
 * Runtime-neutral: shared by the API (response shape), the pathology workspace
 * and provisioning scripts.
 */

/** API / DB row shape (snake_case, like every other case payload). */
export interface SeriesEditBbox {
  id: string
  series_id: string
  /** Stable, caller-chosen id unique per (series, domain) (e.g. `roi-1`). */
  roi_key: string
  /**
   * Edit-region domain the box applies to: a label_raster module's `domain`
   * (e.g. `tissue_segmentation`) or a vector module's `edit_region_domain`;
   * null for every module on the slide that uses edit regions.
   */
  domain: string | null
  label: string | null
  /** Free-text guidance shown to the annotator. */
  note: string | null
  /** Top-left corner in level-0 px of the base slide. */
  x: number
  y: number
  width: number
  height: number
  sort_order: number
}

/** Half-open rectangle `[left, right) × [top, bottom)`. */
export interface PixelRect {
  left: number
  top: number
  right: number
  bottom: number
}

export function editBboxToRect(bbox: Pick<SeriesEditBbox, "x" | "y" | "width" | "height">): PixelRect {
  return { left: bbox.x, top: bbox.y, right: bbox.x + bbox.width, bottom: bbox.y + bbox.height }
}

/** True when two half-open rectangles share at least one pixel. */
export function rectsIntersect(a: PixelRect, b: PixelRect): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom
}

/**
 * Boxes that apply to a module with edit-region `domain` (see
 * `ExtensionHandler.getEditRegionDomain`): domain-less boxes plus boxes
 * declared for exactly that domain. An empty result = unrestricted.
 */
export function editBboxesForDomain<T extends Pick<SeriesEditBbox, "domain">>(
  bboxes: readonly T[],
  domain: string
): T[] {
  return bboxes.filter((bbox) => bbox.domain === null || bbox.domain === undefined || bbox.domain === domain)
}

/** Sort by `sort_order`, then `roi_key` for a stable order. */
export function sortEditBboxes<T extends Pick<SeriesEditBbox, "sort_order" | "roi_key">>(bboxes: readonly T[]): T[] {
  return [...bboxes].sort((a, b) => a.sort_order - b.sort_order || a.roi_key.localeCompare(b.roi_key))
}
